import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#1E2328]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/" className="inline-flex items-center text-[#3aa0e0] hover:text-[#3aa0e0] mb-8 transition-colors">
          ← Back to Home
        </Link>
        
        <h1 className="text-4xl font-bold text-white mb-8">Privacy Policy</h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-slate-400 mb-6">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              FlyFinder ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application.
            </p>
            <p className="text-slate-300 leading-relaxed mb-4">
              By using the FlyFinder app, you consent to the data practices described in this policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-bold text-white mb-3 mt-6">2.1 Personal Information</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide when using the App, including:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>Name and email address (for account creation)</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
              <li>Profile information (optional)</li>
              <li>Contact information when you reach out to customer support</li>
            </ul>

            <h3 className="text-xl font-bold text-white mb-3 mt-6">2.2 Location Data</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              With your permission, we collect precise location data (GPS coordinates) to provide location-based fly recommendations. You can enable or disable location services at any time through your device settings.
            </p>

            <h3 className="text-xl font-bold text-white mb-3 mt-6">2.3 Usage Data</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              We automatically collect certain information when you use the App, including:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>Device type, operating system, and unique device identifiers</li>
              <li>App usage statistics and features accessed</li>
              <li>Crash reports and performance data</li>
              <li>Time and date of your visits</li>
              <li>Search queries within the App</li>
            </ul>

            <h3 className="text-xl font-bold text-white mb-3 mt-6">2.4 Logbook Data</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you use the digital logbook feature, we collect information about:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>Fish catches (species, size, location)</li>
              <li>Flies used and their effectiveness</li>
              <li>Weather conditions at the time of fishing</li>
              <li>Photos you choose to upload</li>
              <li>Personal notes and observations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Provide Services:</strong> To deliver personalized fly recommendations based on your location and preferences</li>
              <li><strong>Improve User Experience:</strong> To analyze usage patterns and improve the App's features and functionality</li>
              <li><strong>Customer Support:</strong> To respond to your inquiries and provide technical support</li>
              <li><strong>Account Management:</strong> To manage your account and subscription</li>
              <li><strong>Communications:</strong> To send you updates, newsletters, and promotional materials (with your consent)</li>
              <li><strong>Analytics:</strong> To understand how users interact with the App and identify areas for improvement</li>
              <li><strong>Security:</strong> To detect, prevent, and address technical issues and fraudulent activity</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. How We Share Your Information</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Service Providers:</strong> We share data with third-party service providers who assist us in operating the App, such as cloud hosting, payment processing, and analytics services</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information if required by law or in response to valid legal requests</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new owner</li>
              <li><strong>With Your Consent:</strong> We may share your information with third parties when you explicitly consent</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. Data Security</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Secure payment processing through industry-standard providers</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Data Retention</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When your information is no longer needed, we will securely delete or anonymize it.
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Account Data:</strong> Retained while your account is active and for a reasonable period after deletion</li>
              <li><strong>Location Data:</strong> Retained temporarily to provide immediate recommendations, then aggregated and anonymized</li>
              <li><strong>Logbook Entries:</strong> Retained as long as you maintain your account or until you delete specific entries</li>
              <li><strong>Usage Analytics:</strong> Retained in aggregated, anonymized form for product improvement</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Your Privacy Rights</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
              <li><strong>Objection:</strong> Object to the processing of your personal information</li>
              <li><strong>Withdraw Consent:</strong> Withdraw your consent for data processing at any time</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              To exercise any of these rights, please contact us at info@flyfinder.ca. We will respond to your request within a reasonable timeframe.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">8. Children's Privacy</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              The FlyFinder App is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe that your child has provided us with personal information, please contact us so we can delete the information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">9. Third-Party Links and Services</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              The App may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">10. International Data Transfers</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Your information may be transferred to and processed in countries other than Canada. We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">12. Contact Us</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-slate-300 leading-relaxed">
              <strong>Email:</strong> <a href="mailto:info@flyfinder.ca" className="text-[#3aa0e0] hover:text-[#3aa0e0]">info@flyfinder.ca</a><br />
              <strong>Subject Line:</strong> Privacy Policy Inquiry
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">13. Consent</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              By using the FlyFinder App, you consent to the collection, use, and disclosure of your information as described in this Privacy Policy.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
